import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGetDocumentArchive } from '../../../services/loanService';
import type { ArchiveBusiness } from '../../../services/schemas/loanServiceSchemas/archiveBusiness';
import { ArchiveHeader, LoanStatusPill, StatCard, formatDateTime, initials, loanStatusLabel } from './archiveShared';

const STATUS_FILTERS = ['ACTIVE', 'IN_ARREARS', 'DEFAULTED', 'CLOSED', 'WRITTEN_OFF'];

/**
 * Document management: the loan document archive. One file per borrower with a disbursed loan, searchable by
 * business, registration number, loan reference or product, with what needs attention at a glance.
 */
const DocumentArchive: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetDocumentArchive({ query: { staleTime: 60_000 } });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [product, setProduct] = useState('');

  const businesses = useMemo(() => data?.businesses || [], [data]);
  const products = useMemo(
    () => Array.from(new Set(businesses.flatMap(b => (b.loans || []).map(l => l.product).filter(Boolean) as string[]))).sort(),
    [businesses]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return businesses.filter((b: ArchiveBusiness) => {
      const loans = b.loans || [];
      const matchesSearch = !term
        || (b.name || '').toLowerCase().includes(term)
        || (b.registrationNumber || '').toLowerCase().includes(term)
        || loans.some(l => (l.accountNumber || '').toLowerCase().includes(term)
          || (l.applicationNumber || '').toLowerCase().includes(term)
          || (l.product || '').toLowerCase().includes(term));
      return matchesSearch
        && (!status || loans.some(l => l.status === status))
        && (!product || loans.some(l => l.product === product));
    });
  }, [businesses, search, status, product]);

  return (
    <>
      <ArchiveHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'All loan files' }]}
        title="Loan document archive"
        subtitle="A permanent, searchable document file for every disbursed loan, organised first by business and then by loan reference."
        actions={
          <Link to="/documents/in-progress" className="btn btn-light shadow-lg px-4 py-2 rounded-pill fw-semibold"
            style={{ color: '#495057', fontSize: '0.9rem' }}>
            <i className="fa fa-hourglass-half me-1"></i>Applications collecting documents
          </Link>
        }
      />
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">

        <div className="row g-3 mb-4">
          <div className="col-6 col-xl-3"><StatCard label="Borrower files" value={data?.borrowerFiles ?? '-'} hint="Businesses with disbursed loans" icon="fa-briefcase" /></div>
          <div className="col-6 col-xl-3"><StatCard label="Loan folders" value={data?.loanFolders ?? '-'} hint="Active, settled and arrears" icon="fa-folder-open-o" /></div>
          <div className="col-6 col-xl-3"><StatCard label="Documents retained" value={data?.documentsRetained ?? '-'} hint="Across the full loan lifecycle" icon="fa-file-o" /></div>
          <div className="col-6 col-xl-3"><StatCard label="Require attention" value={data?.requireAttention ?? '-'} hint="Missing, expired or due for review" icon="fa-exclamation-circle" tone="amber" /></div>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
          <div className="card-body border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-0">All business loan files</h5>
              <small className="text-muted">Search by business, registration number, loan reference or product.</small>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <div className="input-group" style={{ width: 300 }}>
                <span className="input-group-text bg-white"><i className="fa fa-search text-muted"></i></span>
                <input className="form-control" placeholder="Search all loan files..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select className="form-select" style={{ width: 'auto' }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {STATUS_FILTERS.map(s => <option key={s} value={s}>{loanStatusLabel(s)}</option>)}
              </select>
              <select className="form-select" style={{ width: 'auto' }} value={product} onChange={e => setProduct(e.target.value)}>
                <option value="">All products</option>
                {products.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>
          ) : isError ? (
            <div className="text-center py-5 text-muted">
              Could not load the archive. <button className="btn btn-link p-0" onClick={() => refetch()}>Try again</button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="small text-muted">
                  <tr><th className="ps-4">Business folder</th><th>Loan folders</th><th>Document health</th><th>Last updated</th><th></th></tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted py-4">
                      {businesses.length === 0 ? 'No disbursed loans yet.' : 'No loan files match.'}
                    </td></tr>
                  )}
                  {shown.map(b => {
                    const loans = b.loans || [];
                    const statuses = Array.from(new Set(loans.map(l => l.status)));
                    return (
                      <tr key={b.customerPartyId} role="button" onClick={() => navigate(`/documents/files/${b.customerPartyId}`)}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center gap-3">
                            <div className="rounded-3 text-white fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: 48, height: 48, background: '#14281f' }}>{initials(b.name)}</div>
                            <div>
                              <div className="fw-semibold">{b.name || 'Borrower'}</div>
                              <small className="text-muted">{b.registrationNumber}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{loans.length} loan{loans.length === 1 ? '' : 's'}</div>
                          <div className="d-flex gap-1 flex-wrap mt-1">{statuses.map(s => <LoanStatusPill key={s} status={s} />)}</div>
                        </td>
                        <td>
                          <div className="fw-semibold">{b.documentCount} documents</div>
                          {b.attentionCount ? (
                            <small className="text-warning-emphasis"><i className="fa fa-exclamation-circle me-1"></i>{b.attentionCount} require attention</small>
                          ) : (
                            <small className="text-success"><i className="fa fa-check-circle me-1"></i>Complete and current</small>
                          )}
                        </td>
                        <td>
                          <div>{formatDateTime(b.lastUpdated) || '-'}</div>
                          {b.lastUpdatedBy && <small className="text-muted">by {b.lastUpdatedBy}</small>}
                        </td>
                        <td className="text-end pe-4">
                          <button className="btn btn-sm btn-outline-secondary" onClick={e => { e.stopPropagation(); navigate(`/documents/files/${b.customerPartyId}`); }}>
                            <i className="fa fa-folder-open-o me-1"></i>Open file
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default DocumentArchive;
